import React from 'react';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { type CashFlowStatement } from '../../services/api';

interface CashFlowReportProps {
  data?: CashFlowStatement;
  isLoading: boolean;
  dateRange: { startDate: string; endDate: string };
}

const CashFlowReport: React.FC<CashFlowReportProps> = ({ data, isLoading, dateRange }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No data available for the selected period</p>
      </div>
    );
  }

  const renderCashFlowSection = (title: string, data: any, color: string) => (
    <div className={`${color} p-4 rounded-lg`}>
      <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
      <div className="space-y-2">
        {data.items.map((item: any, index: number) => (
          <div key={index} className="flex justify-between items-center">
            <div>
              <span className="text-sm">{item.description}</span>
              <span className="text-xs text-gray-500 ml-2">
                {new Date(item.date).toLocaleDateString()}
              </span>
            </div>
            <span className={`text-sm font-medium ${
              item.type === 'inflow' ? 'text-green-600' : 'text-red-600'
            }`}>
              {item.type === 'inflow' ? '+' : '-'}{formatCurrency(item.amount)}
            </span>
          </div>
        ))}
        <div className="border-t pt-2 mt-2">
          <div className="flex justify-between items-center">
            <span className="text-sm">Total Inflows</span>
            <span className="text-sm font-medium text-green-600">
              {formatCurrency(data.inflows)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm">Total Outflows</span>
            <span className="text-sm font-medium text-red-600">
              ({formatCurrency(data.outflows)})
            </span>
          </div>
          <div className="flex justify-between items-center font-semibold pt-1">
            <span>Net {title}</span>
            <span className={data.net >= 0 ? 'text-green-600' : 'text-red-600'}>
              {data.net >= 0 ? '+' : ''}{formatCurrency(data.net)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Cash Flow Statement</h3>
          <p className="text-sm text-gray-500">
            {new Date(data.period.startDate).toLocaleDateString()} - {new Date(data.period.endDate).toLocaleDateString()}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Net Cash Flow</p>
          <p className={`text-2xl font-bold ${data.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {data.netCashFlow >= 0 ? '+' : ''}{formatCurrency(data.netCashFlow)}
          </p>
        </div>
      </div>

      {/* Cash Balance Summary */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-semibold text-gray-900 mb-3">Cash Balance Summary</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-600">Beginning Balance</span>
            <p className="text-lg font-semibold">{formatCurrency(data.cashBalance.beginning)}</p>
          </div>
          <div>
            <span className="text-sm text-gray-600">Ending Balance</span>
            <p className="text-lg font-semibold">{formatCurrency(data.cashBalance.ending)}</p>
          </div>
        </div>
      </div>

      {/* Cash Flow Activities */}
      <div className="space-y-4">
        {renderCashFlowSection('Operating Activities', data.operatingActivities, 'bg-blue-50')}
        {renderCashFlowSection('Investing Activities', data.investingActivities, 'bg-purple-50')}
        {renderCashFlowSection('Financing Activities', data.financingActivities, 'bg-orange-50')}
      </div>

      {/* Net Cash Flow Summary */}
      <div className={`p-4 rounded-lg ${data.netCashFlow >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
        <div className="flex justify-between items-center">
          <span className="font-semibold text-gray-900">Net Cash Flow</span>
          <div className="flex items-center gap-2">
            {data.netCashFlow >= 0 ? 
              <TrendingUp className="w-5 h-5 text-green-600" /> : 
              <TrendingDown className="w-5 h-5 text-red-600" />
            }
            <span className={`text-xl font-bold ${data.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {data.netCashFlow >= 0 ? '+' : ''}{formatCurrency(data.netCashFlow)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowReport;
